import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MapPin, Users, FileText, Clock, Plus,
  UserPlus, Star, Building2, Trash2, Edit2, Save, X, ChevronRight,
  MessageSquare, StickyNote, PhoneCall, UserCircle2, CalendarDays, FileSignature,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import StatusBadge from "./StatusBadge";
import { RechnungEmailBox } from "./ERechnungStammdaten";
import { EinwilligungEinrichtungBox, OffeneRechnungenBox } from "./EinrichtungEinwilligung";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const ROLLE_LABEL = {
  pdl: "PDL",
  stellv_pdl: "Stellv. PDL",
  gf: "Geschäftsführung",
  hr: "HR / Personal",
  praxisanleitung: "Praxisanleitung",
  einrichtungsleitung: "Einrichtungsleitung",
  assistenz: "Assistenz",
  buchhaltung: "Buchhaltung",
  sonstige: "Sonstige",
};

const ROLLE_COLOR = {
  pdl: "bg-[#FBEDE7] text-[#9A3412] border-[#EBC9B9]",
  stellv_pdl: "bg-[#FBEDE7] text-[#9A3412] border-[#EBC9B9]",
  gf: "bg-[#2a2118] text-[#FBEDE7] border-[#2a2118]",
  hr: "bg-[#e0efd9] text-[#4a6b2b] border-[#c9dcbf]",
  praxisanleitung: "bg-[#dde8f5] text-[#2b5a8a] border-[#b3c6e6]",
  einrichtungsleitung: "bg-[#2a2118] text-[#FBEDE7] border-[#2a2118]",
  assistenz: "bg-[#f5f1ea] text-[#5a4a35] border-[#e6d9c1]",
  buchhaltung: "bg-[#f5f1ea] text-[#5a4a35] border-[#e6d9c1]",
  sonstige: "bg-[#f5f1ea] text-[#5a4a35] border-[#e6d9c1]",
};

const ANGEBOT_STATUS_OPTIONS = [
  { value: "erstellt",       label: "Erstellt",     tone: "bg-[#f5f1ea] text-[#5a4a35] border-[#e6d9c1]" },
  { value: "angebot_raus",   label: "Angebot raus", tone: "bg-[#FBEDE7] text-[#9A3412] border-[#EBC9B9]" },
  { value: "in_klaerung",    label: "In Klärung",   tone: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]" },
  { value: "interesse",      label: "Interesse",    tone: "bg-[#e0efd9] text-[#4a6b2b] border-[#c9dcbf]" },
  { value: "pruefung",       label: "Prüfung",      tone: "bg-[#dde8f5] text-[#2b5a8a] border-[#b3c6e6]" },
  { value: "angenommen",     label: "Angenommen",   tone: "bg-[#d1fae5] text-[#065f46] border-[#a7f3d0]" },
  { value: "abgelehnt",      label: "Abgelehnt",    tone: "bg-[#fee2e2] text-[#991b1b] border-[#fecaca]" },
  { value: "storniert",      label: "Storniert",    tone: "bg-[#e5e7eb] text-[#374151] border-[#d1d5db]" },
];
const ANGEBOT_STATUS_MAP = Object.fromEntries(ANGEBOT_STATUS_OPTIONS.map(o => [o.value, o]));

const formatDateDe = (iso) => {
  if (!iso) return "";
  const s = String(iso).split("T")[0];
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
};

const KANAL_COLOR = {
  telefon: "text-[#2b5a8a]",
  email: "text-[#9A3412]",
  online: "text-[#065f46]",
  persoenlich: "text-[#5a3d1a]",
};

const PflegeeinrichtungDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [apDialogOpen, setApDialogOpen] = useState(false);
  const [editingAp, setEditingAp] = useState(null);
  const [apDubletten, setApDubletten] = useState([]);
  const [apDublettenIgnoriert, setApDublettenIgnoriert] = useState(false);
  const [apForm, setApForm] = useState({
    vorname: "", nachname: "", rolle: "pdl", rolle_frei: "",
    email: "", telefon: "", mobil: "", notizen: "", ist_hauptkontakt: false,
  });
  const [nameEdit, setNameEdit] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [addrDialogOpen, setAddrDialogOpen] = useState(false);
  const [addrForm, setAddrForm] = useState({ strasse: "", plz: "", ort: "", ansprechpartner_telefon: "" });

  const [notizDirty, setNotizDirty] = useState(false);
  const [notizValue, setNotizValue] = useState("");
  const [wvGrundValue, setWvGrundValue] = useState("");
  const [wvGrundDirty, setWvGrundDirty] = useState(false);

  // Aktivitäts-Dialog (Kontaktprotokoll)
  const [aktDialogOpen, setAktDialogOpen] = useState(false);
  const [aktForm, setAktForm] = useState({
    kontaktkanal: "telefon",
    kontaktart: "akquise",
    kontaktdatum: new Date().toISOString().split("T")[0],
    ansprechpartner: "",
    gespraechsinhalt: "",
    ergebnis: "",
    naechster_schritt: "",
    naechster_kontakt_am: "",
  });
  const KANAL_LABEL = {
    telefon: "Telefon",
    email: "E-Mail",
    persoenlich: "Vor Ort",
    online: "Online / WhatsApp",
  };
  const KANAL_ICON = {
    telefon: PhoneCall,
    email: Mail,
    persoenlich: MapPin,
    online: MessageSquare,
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pflegeeinrichtungen/${id}/detail`);
      setDetail(res.data);
      setNotizValue(res.data.einrichtung?.interne_notizen || "");
      setNotizDirty(false);
      setWvGrundValue(res.data.einrichtung?.wiedervorlage_grund || "");
      setWvGrundDirty(false);
    } catch (err) {
      toast.error("Detail-Daten konnten nicht geladen werden");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const resetApForm = () => {
    setEditingAp(null);
    setApDubletten([]);
    setApDublettenIgnoriert(false);
    setApForm({
      vorname: "", nachname: "", rolle: "pdl", rolle_frei: "",
      email: "", telefon: "", mobil: "", notizen: "", ist_hauptkontakt: false,
    });
  };

  // Live-Dubletten-Suche beim Tippen von Vor-/Nachname (nur bei Neu-Anlegen, nicht beim Bearbeiten)
  useEffect(() => {
    if (editingAp || !apDialogOpen) {
      setApDubletten([]);
      return;
    }
    const q = `${apForm.vorname || ""} ${apForm.nachname || ""}`.trim();
    if (q.length < 3) {
      setApDubletten([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/ansprechpartner/suche`, {
          params: { q, exclude_pe_id: id },
        });
        setApDubletten(res.data.treffer || []);
      } catch {
        setApDubletten([]);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [apForm.vorname, apForm.nachname, editingAp, apDialogOpen, id]);

  const openApDialog = (ap = null) => {
    if (ap) {
      setEditingAp(ap);
      setApForm({
        vorname: ap.vorname || "",
        nachname: ap.nachname || "",
        rolle: ap.rolle || "pdl",
        rolle_frei: ap.rolle_frei || "",
        email: ap.email || "",
        telefon: ap.telefon || "",
        mobil: ap.mobil || "",
        notizen: ap.notizen || "",
        ist_hauptkontakt: !!ap.ist_hauptkontakt,
      });
    } else {
      resetApForm();
    }
    setApDialogOpen(true);
  };

  const saveAp = async () => {
    try {
      if (!apForm.vorname && !apForm.nachname) {
        toast.error("Bitte mindestens Vor- oder Nachname angeben");
        return;
      }
      // Dubletten-Warnung: Wenn Treffer vorhanden und noch nicht ignoriert → nachfragen
      if (!editingAp && apDubletten.length > 0 && !apDublettenIgnoriert) {
        const namen = apDubletten.slice(0, 3).map(t => `${t.vorname} ${t.nachname} @ ${t.einrichtungsname}`).join("\n");
        const weiter = window.confirm(
          `Ähnliche Ansprechpartner gefunden:\n\n${namen}${apDubletten.length > 3 ? `\n... und ${apDubletten.length - 3} weitere` : ""}\n\nTrotzdem als neuen Kontakt anlegen?`
        );
        if (!weiter) {
          setApDublettenIgnoriert(false);
          return;
        }
        setApDublettenIgnoriert(true);
      }
      if (editingAp) {
        await axios.patch(
          `${API}/pflegeeinrichtungen/${id}/ansprechpartner/${editingAp.id}`,
          apForm
        );
        toast.success("Ansprechpartner aktualisiert");
      } else {
        await axios.post(
          `${API}/pflegeeinrichtungen/${id}/ansprechpartner`,
          { ...apForm, pflegeeinrichtung_id: id }
        );
        toast.success("Ansprechpartner angelegt");
      }
      setApDialogOpen(false);
      resetApForm();
      load();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
      console.error(err);
    }
  };

  const deleteAp = async (ap) => {
    if (!window.confirm(`Ansprechpartner „${ap.vorname} ${ap.nachname}" wirklich entfernen?\n\n(Datensatz bleibt archiviert, kein Datenverlust)`)) return;
    try {
      await axios.delete(`${API}/pflegeeinrichtungen/${id}/ansprechpartner/${ap.id}`);
      toast.success("Ansprechpartner archiviert");
      load();
    } catch (err) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const saveNotiz = async () => {
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${id}`, {
        ...detail.einrichtung,
        interne_notizen: notizValue,
      });
      toast.success("Notiz gespeichert");
      setNotizDirty(false);
      load();
    } catch (err) {
      toast.error("Notiz konnte nicht gespeichert werden");
    }
  };

  const saveName = async () => {
    const trimmed = (nameValue || "").trim();
    if (!trimmed) {
      toast.error("Name darf nicht leer sein");
      return;
    }
    if (trimmed === detail.einrichtung.einrichtungsname) {
      setNameEdit(false);
      return;
    }
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${id}`, {
        ...detail.einrichtung,
        einrichtungsname: trimmed,
      });
      toast.success("Name aktualisiert");
      setNameEdit(false);
      load();
    } catch {
      toast.error("Name konnte nicht gespeichert werden");
    }
  };

  const openAddrDialog = () => {
    const e = detail.einrichtung || {};
    setAddrForm({
      strasse: e.strasse || "",
      plz: e.plz || "",
      ort: e.ort || "",
      ansprechpartner_telefon: e.ansprechpartner_telefon || "",
    });
    setAddrDialogOpen(true);
  };

  const saveAddr = async () => {
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${id}`, {
        ...detail.einrichtung,
        ...addrForm,
      });
      toast.success("Stammdaten aktualisiert");
      setAddrDialogOpen(false);
      load();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const setWiedervorlage = async (isoDate, grund) => {
    try {
      const payload = { naechste_wiedervorlage: isoDate || null };
      if (typeof grund === "string") payload.wiedervorlage_grund = grund;
      await axios.patch(`${API}/pflegeeinrichtungen/${id}/wiedervorlage`, payload);
      toast.success(isoDate ? `Wiedervorlage gesetzt: ${formatDateDe(isoDate)}` : "Wiedervorlage entfernt");
      load();
    } catch (err) {
      toast.error("Wiedervorlage konnte nicht gesetzt werden");
    }
  };

  const saveWvGrund = async () => {
    try {
      await axios.patch(`${API}/pflegeeinrichtungen/${id}/wiedervorlage`, {
        naechste_wiedervorlage: detail?.einrichtung?.naechste_wiedervorlage || null,
        wiedervorlage_grund: wvGrundValue,
      });
      toast.success("Grund gespeichert");
      setWvGrundDirty(false);
      load();
    } catch {
      toast.error("Grund konnte nicht gespeichert werden");
    }
  };

  const updateAngebotStatus = async (angebotId, newStatus) => {
    try {
      await axios.patch(`${API}/angebote/${angebotId}/status`, { status: newStatus });
      toast.success(`Status: ${ANGEBOT_STATUS_MAP[newStatus]?.label || newStatus}`);
      load();
    } catch {
      toast.error("Status konnte nicht geändert werden");
    }
  };

  const openAktDialog = (kanal = "telefon", art = "akquise") => {
    // Vorbelegung: primärer Ansprechpartner (neu > legacy), heute als Kontaktdatum
    const hauptAp = detail?.ansprechpartner?.find((a) => a.ist_hauptkontakt) || detail?.ansprechpartner?.[0];
    const apName = hauptAp
      ? `${hauptAp.vorname || ""} ${hauptAp.nachname || ""}`.trim()
      : (detail?.legacy_primaer_kontakt?.name || "");
    setAktForm({
      kontaktkanal: kanal,
      kontaktart: art,
      kontaktdatum: new Date().toISOString().split("T")[0],
      ansprechpartner: apName,
      gespraechsinhalt: "",
      ergebnis: "",
      naechster_schritt: "",
      naechster_kontakt_am: "",
    });
    setAktDialogOpen(true);
  };

  const saveAktivitaet = async () => {
    try {
      const payload = {
        pflegeeinrichtung_id: id,
        kontaktkanal: aktForm.kontaktkanal,
        kontaktart: aktForm.kontaktart,
        kontaktdatum: aktForm.kontaktdatum,
        ansprechpartner: aktForm.ansprechpartner,
        gespraechsinhalt: aktForm.gespraechsinhalt,
        ergebnis: aktForm.ergebnis,
        naechster_schritt: aktForm.naechster_schritt,
        naechster_kontakt_am: aktForm.naechster_kontakt_am || null,
      };
      await axios.post(`${API}/kontaktprotokoll`, payload);
      toast.success("Aktivität protokolliert");
      setAktDialogOpen(false);
      // Falls "nächster Kontakt" gesetzt wurde → automatisch als Wiedervorlage setzen
      if (aktForm.naechster_kontakt_am) {
        try {
          await axios.patch(`${API}/pflegeeinrichtungen/${id}/wiedervorlage`, {
            naechste_wiedervorlage: aktForm.naechster_kontakt_am,
            wiedervorlage_grund: aktForm.naechster_schritt || aktForm.ergebnis || "",
          });
        } catch { /* nicht kritisch */ }
      }
      load();
    } catch (err) {
      toast.error("Aktivität konnte nicht gespeichert werden");
      console.error(err);
    }
  };

  // Angebots-Dialog
  const [angebotDialogOpen, setAngebotDialogOpen] = useState(false);
  const [angebotSaving, setAngebotSaving] = useState(false);
  const [angebotForm, setAngebotForm] = useState({
    positionen: [{ beschreibung: "", menge: 1, einzelpreis: 0 }],
    rabatt_art: "keiner",
    rabatt_hoehe: 0,
    notiz: "",
    angebot_text: "",
    gueltig_bis: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  });

  const openAngebotDialog = () => {
    setAngebotForm({
      positionen: [{ beschreibung: "", menge: 1, einzelpreis: 0 }],
      rabatt_art: "keiner",
      rabatt_hoehe: 0,
      notiz: "",
      angebot_text: "",
      gueltig_bis: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    });
    setAngebotDialogOpen(true);
  };

  const angebotZwischensumme = () => {
    return (angebotForm.positionen || []).reduce(
      (sum, p) => sum + (Number(p.menge) || 0) * (Number(p.einzelpreis) || 0),
      0
    );
  };

  const angebotRabattBetrag = () => {
    const zs = angebotZwischensumme();
    if (angebotForm.rabatt_art === "prozent") return (zs * Number(angebotForm.rabatt_hoehe || 0)) / 100;
    if (angebotForm.rabatt_art === "fixbetrag") return Number(angebotForm.rabatt_hoehe || 0);
    return 0;
  };

  const angebotNetto = () => angebotZwischensumme() - angebotRabattBetrag();

  const addPosition = () =>
    setAngebotForm({
      ...angebotForm,
      positionen: [...(angebotForm.positionen || []), { beschreibung: "", menge: 1, einzelpreis: 0 }],
    });

  const removePosition = (idx) =>
    setAngebotForm({
      ...angebotForm,
      positionen: (angebotForm.positionen || []).filter((_, i) => i !== idx),
    });

  const updatePosition = (idx, field, value) => {
    const neu = [...(angebotForm.positionen || [])];
    neu[idx] = { ...neu[idx], [field]: value };
    setAngebotForm({ ...angebotForm, positionen: neu });
  };

  const generateAngebotPdf = async () => {
    try {
      const validPositionen = (angebotForm.positionen || []).filter((p) => p.beschreibung && p.beschreibung.trim().length > 0);
      if (validPositionen.length === 0) {
        toast.error("Bitte mindestens eine Position mit Beschreibung angeben.");
        return;
      }
      setAngebotSaving(true);
      const pe = detail.einrichtung;
      // Primär-Ansprechpartner ermitteln (neuer Hauptkontakt > Legacy)
      const hauptAp = detail?.ansprechpartner?.find((a) => a.ist_hauptkontakt) || detail?.ansprechpartner?.[0];
      const apName = hauptAp
        ? `${hauptAp.vorname || ""} ${hauptAp.nachname || ""}`.trim()
        : (detail?.legacy_primaer_kontakt?.name || pe.ansprechpartner_name || "");

      const payload = {
        pflegeeinrichtung_id: pe.id,
        empfaenger_name: pe.einrichtungsname,
        empfaenger_strasse: pe.strasse || "",
        empfaenger_plz: pe.plz || "",
        empfaenger_ort: pe.ort || "",
        ansprechpartner: apName,
        positionen: validPositionen.map((p) => ({
          beschreibung: p.beschreibung,
          menge: Number(p.menge) || 1,
          einzelpreis: Number(p.einzelpreis) || 0,
          rabatt_art: "keiner",
          rabatt_wert: 0,
          ist_geschenk: false,
        })),
        rabatt_art: angebotForm.rabatt_art,
        rabatt_hoehe: Number(angebotForm.rabatt_hoehe) || 0,
        rabatt_betrag: angebotRabattBetrag(),
        notiz: angebotForm.notiz || "",
        angebot_text: angebotForm.angebot_text || "",
        gueltig_bis: angebotForm.gueltig_bis || "",
      };
      const res = await axios.post(`${API}/angebote/pdf`, payload, { responseType: "blob" });
      // PDF direkt zum Download
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safeName = (pe.einrichtungsname || "Einrichtung").replace(/[^a-zA-Z0-9]/g, "_");
      link.download = `Angebot_${safeName}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Angebot erstellt und PDF heruntergeladen");
      setAngebotDialogOpen(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error("Angebot konnte nicht erstellt werden");
    } finally {
      setAngebotSaving(false);
    }
  };

  const wvSchnellButton = (label, days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const iso = d.toISOString().split("T")[0];
    return (
      <button
        key={label}
        onClick={() => setWiedervorlage(iso)}
        className="px-3 py-1.5 bg-white border border-[#d6c4a5] rounded-md text-xs text-[#5a4a35] hover:bg-[#FBEDE7] hover:border-[#9A3412] transition-colors"
        data-testid={`wv-quick-${label.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
      >
        {label}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!detail || !detail.einrichtung) {
    return (
      <div className="page-container">
        <p>Einrichtung nicht gefunden.</p>
        <Button onClick={() => navigate("/pflegeeinrichtungen")}>Zurück</Button>
      </div>
    );
  }

  const pe = detail.einrichtung;
  const legacy = detail.legacy_primaer_kontakt;

  return (
    <div className="page-container" data-testid="pflegeeinrichtung-detail-page">
      <button
        onClick={() => navigate("/pflegeeinrichtungen")}
        className="flex items-center text-sm text-[#9A3412] hover:text-[#5a3d1a] mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} className="mr-1" />
        Zurück zur Liste
      </button>

      {/* Großer warmer Container — alles darin, wie im Mockup */}
      <div className="bg-[#F7F5F2] border border-[#efe4d1] rounded-2xl p-6 md:p-8 mb-6" data-testid="pe-detail-container">
        {/* Kopf */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 group">
              <Building2 size={22} className="text-[#9A3412] flex-shrink-0" />
              {nameEdit ? (
                <input
                  autoFocus
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName();
                    if (e.key === "Escape") { setNameEdit(false); setNameValue(pe.einrichtungsname); }
                  }}
                  className="text-3xl md:text-4xl font-semibold text-[#2a2118] bg-transparent border-b-2 border-[#9A3412] outline-none min-w-0 flex-1"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                  data-testid="einrichtungsname-input"
                />
              ) : (
                <h1
                  className="text-3xl md:text-4xl font-semibold text-[#2a2118] break-words leading-tight cursor-pointer hover:bg-[#FBEDE7] hover:px-1 hover:-mx-1 rounded transition"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                  onClick={() => { setNameValue(pe.einrichtungsname); setNameEdit(true); }}
                  title="Klicken zum Umbenennen"
                  data-testid="einrichtungsname-h1"
                >
                  {pe.einrichtungsname}
                </h1>
              )}
              {pe.hot_lead && <Star size={20} className="text-amber-500 fill-amber-500 ml-1" />}
              {!nameEdit && (
                <button
                  onClick={() => { setNameValue(pe.einrichtungsname); setNameEdit(true); }}
                  className="p-1 text-[#78716c] hover:text-[#9A3412] opacity-0 group-hover:opacity-100 transition"
                  title="Name umbenennen"
                  data-testid="edit-name-btn"
                >
                  <Edit2 size={14} />
                </button>
              )}
            </div>
            <div className="text-sm text-[#78716c] flex flex-wrap items-center gap-x-3 gap-y-1 pl-8">
              {pe.akquise_status && <span className="capitalize">{pe.akquise_status.replace(/_/g, " ")}</span>}
              {detail.vertriebsmitarbeiter_name && (
                <span data-testid="badge-vm">· zugewiesen an <b className="text-[#5a4a35]">{detail.vertriebsmitarbeiter_name}</b></span>
              )}
              {pe.letzter_kontakt_am && (
                <span data-testid="badge-letzter-kontakt">· zuletzt kontaktiert {formatDateDe(pe.letzter_kontakt_am)}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-[#78716c] pl-8 mt-1">
              <MapPin size={12} />
              <button
                onClick={openAddrDialog}
                className="hover:text-[#9A3412] hover:underline text-left"
                title="Adresse bearbeiten"
                data-testid="edit-addr-btn"
              >
                {[pe.strasse, pe.plz, pe.ort].filter(Boolean).join(", ") || "Adresse hinzufügen"}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {pe.naechste_wiedervorlage && (
              <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#FBEDE7] text-[#9A3412] border border-[#EBC9B9] flex items-center gap-1.5" data-testid="badge-wv">
                <Clock size={13} />
                Wiedervorlage {formatDateDe(pe.naechste_wiedervorlage).replace(/\.\d{4}$/, ".")}
              </span>
            )}
            {detail.offene_angebote_count > 0 && (
              <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#e0efd9] text-[#4a6b2b] border border-[#c9dcbf] flex items-center gap-1.5" data-testid="badge-offene-angebote">
                <FileSignature size={13} />
                {detail.offene_angebote_count} offene Angebote
              </span>
            )}
            {pe.ist_neukunde && (
              <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#e0efd9] text-[#4a6b2b] border border-[#c9dcbf]">
                Neukunde{pe.neukunde_seit ? ` seit ${formatDateDe(pe.neukunde_seit)}` : ""}
              </span>
            )}
            {pe.md_pruefungsnote && (
              <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-[#e6d9c1] text-[#5a4a35]">
                MD: {pe.md_pruefungsnote}
              </span>
            )}
          </div>
        </div>

        {/* Zwei-Spalten: Ansprechpartner + Wiedervorlage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          {/* Ansprechpartner — flache Liste */}
          <div className="bg-white border border-[#F6E3DA] rounded-xl p-5" data-testid="ap-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412]">
                Ansprechpartner
              </h2>
              <button
                onClick={() => openApDialog()}
                className="text-xs text-[#9A3412] hover:text-[#5a3d1a] flex items-center gap-1"
                data-testid="add-ansprechpartner-btn"
              >
                <UserPlus size={13} />
                Hinzufügen
              </button>
            </div>

            <div className="divide-y divide-[#F6E3DA]">
              {legacy && (
                <div className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className="font-medium text-[#2a2118]">{legacy.name}</span>
                    <span className="text-[11px] text-[#9A3412]">Primär (klassisch)</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 text-xs text-[#78716c]">
                    <span>{legacy.telefon || ""}</span>
                    <span className="truncate">{legacy.email || ""}</span>
                  </div>
                </div>
              )}

              {detail.ansprechpartner.map((ap) => {
                const rolleLbl = ap.rolle === "sonstige" && ap.rolle_frei ? ap.rolle_frei : ROLLE_LABEL[ap.rolle];
                const tel = ap.mobil || ap.telefon;
                return (
                  <div key={ap.id} className="py-3 first:pt-0 last:pb-0 group" data-testid={`ap-${ap.id}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="font-medium text-[#2a2118] truncate">
                          {[ap.vorname, ap.nachname].filter(Boolean).join(" ") || "—"}
                        </span>
                        {ap.ist_hauptkontakt && (
                          <span className="text-[10px] text-amber-800"></span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-[#9A3412] uppercase tracking-wide">{rolleLbl}</span>
                        <button onClick={() => openApDialog(ap)} className="p-0.5 text-[#78716c] hover:text-[#9A3412] opacity-0 group-hover:opacity-100 transition" title="Bearbeiten" data-testid={`edit-ap-${ap.id}`}>
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => deleteAp(ap)} className="p-0.5 text-[#78716c] hover:text-red-600 opacity-0 group-hover:opacity-100 transition" title="Archivieren" data-testid={`del-ap-${ap.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 text-xs text-[#78716c]">
                      <span className="truncate">{tel || ""}</span>
                      <span className="truncate">{ap.email || ""}</span>
                    </div>
                    {ap.notizen && <div className="text-xs italic text-[#5a4a35] mt-1">{ap.notizen}</div>}
                  </div>
                );
              })}

              {!legacy && detail.ansprechpartner.length === 0 && (
                <p className="text-sm text-[#78716c] italic py-2">Noch keine Ansprechpartner erfasst.</p>
              )}
            </div>
          </div>

          {/* Wiedervorlage */}
          <div className="bg-white border border-[#F6E3DA] rounded-xl p-5" data-testid="wv-card">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412] mb-3">
              Nächste Wiedervorlage
            </h2>
            <div className="flex items-center gap-2 mb-3">
              <Input
                type="date"
                value={pe.naechste_wiedervorlage || ""}
                onChange={(e) => setWiedervorlage(e.target.value)}
                className="w-auto"
                data-testid="wv-date-input"
              />
              {pe.naechste_wiedervorlage && (
                <button
                  onClick={() => setWiedervorlage(null, "")}
                  className="text-xs text-[#78716c] hover:text-red-600"
                  data-testid="wv-clear-btn"
                >
                  Entfernen
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {wvSchnellButton("Heute", 0)}
              {wvSchnellButton("Morgen", 1)}
              {wvSchnellButton("+7 Tage", 7)}
              {wvSchnellButton("+14 Tage", 14)}
              {wvSchnellButton("+30 Tage", 30)}
            </div>

            {/* Grund — inline im Mockup-Look */}
            <div className="text-sm text-[#5a4a35]">
              <span className="text-[#78716c]">Grund:&nbsp;</span>
              <input
                type="text"
                value={wvGrundValue}
                onChange={(e) => { setWvGrundValue(e.target.value); setWvGrundDirty(true); }}
                onBlur={() => { if (wvGrundDirty) saveWvGrund(); }}
                placeholder="z.B. Rückruf gewünscht"
                className="inline bg-transparent border-none outline-none focus:bg-[#F7F5F2] focus:px-1 focus:rounded transition text-[#2a2118] placeholder:text-[#a89f92] min-w-[200px]"
                data-testid="wv-grund-input"
              />
              {wvGrundDirty && (
                <span className="text-[10px] text-[#9A3412] ml-2">(wird beim Verlassen gespeichert)</span>
              )}
            </div>
          </div>
        </div>

        {/* Notiz — eigene volle Sektion */}
        <div className="bg-white border border-[#F6E3DA] rounded-xl p-5 mb-5" data-testid="notiz-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412]">
              Notiz — direkt eintragen und speichern
            </h2>
            {notizDirty && (
              <Button size="sm" onClick={saveNotiz} data-testid="save-notiz-btn">
                <Save size={13} className="mr-1" />
                Speichern
              </Button>
            )}
          </div>
          <Textarea
            value={notizValue}
            onChange={(e) => { setNotizValue(e.target.value); setNotizDirty(true); }}
            placeholder="Was ist beim letzten Kontakt passiert? Was ist zu tun?"
            rows={3}
            className="border-[#F6E3DA] focus:border-[#9A3412]"
            data-testid="notiz-textarea"
          />
        </div>

        {/* Bestellungen / Angebote — Tabellen-Style */}
        <div className="bg-white border border-[#F6E3DA] rounded-xl p-5 mb-5" data-testid="angebote-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412]">
              Bestellungen / Angebote pro Einrichtung
            </h2>
            <button
              onClick={openAngebotDialog}
              className="text-xs text-[#9A3412] hover:text-[#5a3d1a] flex items-center gap-1"
              data-testid="btn-neues-angebot"
            >
              <Plus size={13} />
              Neue Bestellung anlegen
            </button>
          </div>
          {detail.angebote.length === 0 ? (
            <p className="text-sm text-[#78716c] italic py-1">Noch keine Bestellungen für diese Einrichtung.</p>
          ) : (
            <div className="divide-y divide-[#F6E3DA]">
              {detail.angebote.map((a) => {
                const status = (a.status || "erstellt").toLowerCase();
                const statusMeta = ANGEBOT_STATUS_MAP[status] || ANGEBOT_STATUS_MAP.erstellt;
                const gesendet = ["angebot_raus", "in_klaerung", "angenommen", "abgelehnt"].includes(status);
                let netto = a.gesamt_netto;
                if (netto == null && Array.isArray(a.positionen) && a.positionen.length) {
                  const zs = a.positionen.reduce((sum, p) => sum + (Number(p.menge) || 0) * (Number(p.einzelpreis) || 0), 0);
                  const rabatt =
                    a.rabatt_art === "prozent" ? (zs * Number(a.rabatt_hoehe || 0)) / 100 :
                    a.rabatt_art === "fixbetrag" ? Number(a.rabatt_hoehe || 0) : 0;
                  netto = zs - rabatt;
                }
                return (
                  <div key={a.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 py-3 first:pt-0 last:pb-0 text-sm" data-testid={`angebot-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-[#2a2118] truncate">
                        {a.titel || a.kurs_name || a.angebotsnummer || "Angebot"}
                      </div>
                    </div>
                    <div className="text-[#2a2118] font-medium tabular-nums whitespace-nowrap">
                      {netto != null ? `${Number(netto).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €` : "—"}
                    </div>
                    {gesendet ? (
                      <span className="text-[11px] px-2 py-0.5 rounded bg-[#f5f1ea] text-[#5a4a35] border border-[#e6d9c1] whitespace-nowrap">
                        Angebot raus
                      </span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded bg-white text-[#78716c] border border-[#F6E3DA] whitespace-nowrap">
                        {formatDateDe(a.created_at)}
                      </span>
                    )}
                    <Select value={status} onValueChange={(v) => updateAngebotStatus(a.id, v)}>
                      <SelectTrigger className={`h-7 px-2 text-[11px] font-medium border rounded ${statusMeta.tone} min-w-[110px]`} data-testid={`angebot-status-select-${a.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ANGEBOT_STATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Aktivitäts-Historie */}
        <div className="bg-white border border-[#F6E3DA] rounded-xl p-5" data-testid="historie-card">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412] mb-3">
            Aktivitäts-Historie — direkt hier ergänzen
          </h2>

          {/* Schnellauswahl — schwarze Pill für Anruf, Outline für Rest */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => openAktDialog("telefon", "akquise")}
              className="px-4 py-1.5 bg-[#2a2118] text-white rounded-full text-xs font-medium hover:bg-black transition flex items-center gap-1.5"
              data-testid="btn-quick-anruf"
            >
              <PhoneCall size={12} />
              + Anruf
            </button>
            <button
              onClick={() => openAktDialog("online", "akquise")}
              className="px-4 py-1.5 bg-white border border-[#e6d9c1] text-[#5a4a35] rounded-full text-xs font-medium hover:border-[#9A3412] transition flex items-center gap-1.5"
              data-testid="btn-quick-whatsapp"
            >
              <MessageSquare size={12} />
              + WhatsApp
            </button>
            <button
              onClick={() => openAktDialog("email", "akquise")}
              className="px-4 py-1.5 bg-white border border-[#e6d9c1] text-[#5a4a35] rounded-full text-xs font-medium hover:border-[#9A3412] transition flex items-center gap-1.5"
              data-testid="btn-quick-email"
            >
              <Mail size={12} />
              + E-Mail
            </button>
            <button
              onClick={() => openAktDialog("persoenlich", "akquise")}
              className="px-4 py-1.5 bg-white border border-[#e6d9c1] text-[#5a4a35] rounded-full text-xs font-medium hover:border-[#9A3412] transition flex items-center gap-1.5"
              data-testid="btn-quick-vorort"
            >
              <MapPin size={12} />
              + Termin
            </button>
            <button
              onClick={() => openAktDialog("online", "bestandskunde")}
              className="px-4 py-1.5 bg-white border border-[#e6d9c1] text-[#5a4a35] rounded-full text-xs font-medium hover:border-[#9A3412] transition flex items-center gap-1.5"
              data-testid="btn-quick-notiz"
            >
              <StickyNote size={12} />
              + Notiz
            </button>
          </div>

          {detail.kontakte.length === 0 ? (
            <p className="text-sm text-[#78716c] italic">Noch keine Kontakte protokolliert.</p>
          ) : (
            <div className="divide-y divide-[#F6E3DA]">
              {detail.kontakte.slice(0, 30).map((k) => {
                const kanalLabel = KANAL_LABEL[k.kontaktkanal] || k.kontaktkanal;
                const kanalColor = KANAL_COLOR[k.kontaktkanal] || "text-[#9A3412]";
                const dt = k.kontaktdatum ? new Date(k.kontaktdatum) : null;
                const dateStr = dt ? `${String(dt.getDate()).padStart(2,"0")}.${String(dt.getMonth()+1).padStart(2,"0")}.` : "-";
                const timeStr = dt && k.kontaktdatum?.includes("T") && !k.kontaktdatum.endsWith("T00:00:00")
                  ? `${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`
                  : "";
                return (
                  <div key={k.id} className="grid grid-cols-[105px_1fr] gap-3 py-2 first:pt-0 last:pb-0 text-sm items-baseline">
                    <div className="text-xs text-[#78716c] tabular-nums">
                      {dateStr}{timeStr && <> {timeStr}</>}
                    </div>
                    <div className="text-[#2a2118]">
                      <span className={`font-semibold ${kanalColor}`}>{kanalLabel}</span>
                      {k.ansprechpartner && <span className="text-[#78716c]"> · {k.ansprechpartner}</span>}
                      {(k.gespraechsinhalt || k.ergebnis) && (
                        <span className="text-[#5a4a35]"> — {[k.gespraechsinhalt, k.ergebnis].filter(Boolean).join(" · ")}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {detail.kontakte.length > 30 && (
                <p className="text-xs text-[#78716c] italic pt-2">
                  … und {detail.kontakte.length - 30} weitere Einträge
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Teilnehmer — außerhalb des Warm-Containers, weil Sekundär-Info */}
      <RechnungEmailBox peId={id} />
      <OffeneRechnungenBox rechnungen={detail.offene_rechnungen || []} />
      <EinwilligungEinrichtungBox peId={id} />
      {detail.teilnehmer.length > 0 && (
        <Card className="card-custom mb-5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A3412]">
                <Users size={12} className="inline mr-1" />
                Teilnehmer dieser Einrichtung ({detail.teilnehmer_count})
              </h2>
            </div>
            <div className="space-y-2">
              {detail.teilnehmer.slice(0, 20).map((tn) => (
                <button
                  key={tn.id}
                  onClick={() => navigate(`/teilnehmer?id=${tn.id}`)}
                  className="w-full text-left flex items-center justify-between bg-[#F7F5F2] border border-[#F6E3DA] rounded-lg px-3 py-2 text-sm hover:border-[#9A3412] hover:bg-[#FBEDE7] transition-colors"
                  data-testid={`tn-${tn.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2a2118] truncate">
                      {tn.name || `${tn.vorname || ""} ${tn.nachname || ""}`.trim()}
                    </div>
                    <div className="text-xs text-[#78716c]">
                      {tn.kurs}
                      {tn.kurs_start && <> · Start {formatDateDe(tn.kurs_start)}</>}
                      {tn.zertifikatsnummer && <> · Zert-Nr {tn.zertifikatsnummer}</>}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-[#78716c] flex-shrink-0" />
                </button>
              ))}
              {detail.teilnehmer.length > 20 && (
                <p className="text-xs text-[#78716c] italic pt-1">
                  … und {detail.teilnehmer.length - 20} weitere
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Ansprechpartner-Dialog */}
      <Dialog open={apDialogOpen} onOpenChange={(v) => { setApDialogOpen(v); if (!v) resetApForm(); }}>
        <DialogContent className="max-w-lg bg-white" data-testid="ap-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
              {editingAp ? "Ansprechpartner bearbeiten" : "Neuer Ansprechpartner"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Vorname</Label>
                <Input value={apForm.vorname} onChange={(e) => setApForm({ ...apForm, vorname: e.target.value })} data-testid="ap-vorname" />
              </div>
              <div>
                <Label>Nachname</Label>
                <Input value={apForm.nachname} onChange={(e) => setApForm({ ...apForm, nachname: e.target.value })} data-testid="ap-nachname" />
              </div>
            </div>

            {/* Dubletten-Warnung */}
            {!editingAp && apDubletten.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="ap-dubletten-warn">
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 font-semibold text-sm">{apDubletten.length} ähnliche Ansprechpartner in anderen Einrichtungen</span>
                </div>
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {apDubletten.slice(0, 5).map((t, i) => (
                    <div key={i} className="text-xs text-amber-900 flex items-center justify-between gap-2">
                      <span>
                        <b>{t.vorname} {t.nachname}</b>
                        {t.rolle && t.rolle !== "unbekannt" && <span className="text-amber-700"> · {ROLLE_LABEL[t.rolle] || t.rolle}</span>}
                        <span className="text-amber-700"> @ </span>
                        <span className="italic">{t.einrichtungsname}</span>
                        {t.ort && <span className="text-amber-700"> ({t.ort})</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setApDialogOpen(false); navigate(`/pflegeeinrichtungen/${t.pflegeeinrichtung_id}`); }}
                        className="text-amber-700 hover:text-amber-900 underline whitespace-nowrap"
                        data-testid={`ap-dublette-goto-${i}`}
                      >
                        ansehen
                      </button>
                    </div>
                  ))}
                  {apDubletten.length > 5 && (
                    <div className="text-xs text-amber-700 italic">... und {apDubletten.length - 5} weitere</div>
                  )}
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Prüfe kurz, ob es sich um dieselbe Person handelt. Trotzdem anlegen ist beim Speichern möglich.
                </p>
              </div>
            )}

            <div>
              <Label>Rolle</Label>
              <Select value={apForm.rolle} onValueChange={(v) => setApForm({ ...apForm, rolle: v })}>
                <SelectTrigger data-testid="ap-rolle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLLE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {apForm.rolle === "sonstige" && (
              <div>
                <Label>Rolle (frei)</Label>
                <Input value={apForm.rolle_frei} onChange={(e) => setApForm({ ...apForm, rolle_frei: e.target.value })} placeholder="z.B. Qualitätsmanagement" data-testid="ap-rolle-frei" />
              </div>
            )}
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={apForm.email} onChange={(e) => setApForm({ ...apForm, email: e.target.value })} data-testid="ap-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefon (Festnetz)</Label>
                <Input value={apForm.telefon} onChange={(e) => setApForm({ ...apForm, telefon: e.target.value })} data-testid="ap-telefon" />
              </div>
              <div>
                <Label>Mobil</Label>
                <Input value={apForm.mobil} onChange={(e) => setApForm({ ...apForm, mobil: e.target.value })} data-testid="ap-mobil" />
              </div>
            </div>
            <div>
              <Label>Notizen</Label>
              <Textarea rows={2} value={apForm.notizen} onChange={(e) => setApForm({ ...apForm, notizen: e.target.value })} data-testid="ap-notizen" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={apForm.ist_hauptkontakt}
                onChange={(e) => setApForm({ ...apForm, ist_hauptkontakt: e.target.checked })}
                data-testid="ap-hauptkontakt"
              />
              Als Hauptkontakt markieren
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApDialogOpen(false); resetApForm(); }}>
              <X size={14} className="mr-1" />
              Abbrechen
            </Button>
            <Button onClick={saveAp} data-testid="save-ap-btn">
              <Save size={14} className="mr-1" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Angebots-Dialog */}
      <Dialog open={angebotDialogOpen} onOpenChange={setAngebotDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="angebot-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
              Neues Angebot für {detail.einrichtung.einrichtungsname}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {/* Positionen */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Positionen</Label>
                <Button size="sm" variant="outline" onClick={addPosition} data-testid="btn-add-position">
                  <Plus size={12} className="mr-1" />
                  Zeile
                </Button>
              </div>
              <div className="space-y-2">
                {(angebotForm.positionen || []).map((pos, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_80px_100px_36px] gap-2 items-center" data-testid={`position-${idx}`}>
                    <Input
                      placeholder="Beschreibung (z.B. LG1/LG2 · 4 TN)"
                      value={pos.beschreibung}
                      onChange={(e) => updatePosition(idx, "beschreibung", e.target.value)}
                      data-testid={`pos-desc-${idx}`}
                    />
                    <Input
                      type="number"
                      min="1"
                      placeholder="Menge"
                      value={pos.menge}
                      onChange={(e) => updatePosition(idx, "menge", e.target.value)}
                      data-testid={`pos-menge-${idx}`}
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Einzelpreis €"
                      value={pos.einzelpreis}
                      onChange={(e) => updatePosition(idx, "einzelpreis", e.target.value)}
                      data-testid={`pos-preis-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removePosition(idx)}
                      disabled={(angebotForm.positionen || []).length <= 1}
                      className="p-2 text-[#78716c] hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Zeile entfernen"
                      data-testid={`pos-remove-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Rabatt + Gültigkeit */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Rabatt-Art</Label>
                <Select value={angebotForm.rabatt_art} onValueChange={(v) => setAngebotForm({ ...angebotForm, rabatt_art: v })}>
                  <SelectTrigger data-testid="rabatt-art">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keiner">Kein Rabatt</SelectItem>
                    <SelectItem value="prozent">Prozent %</SelectItem>
                    <SelectItem value="fixbetrag">Fixbetrag €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rabatt-Höhe</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={angebotForm.rabatt_hoehe}
                  onChange={(e) => setAngebotForm({ ...angebotForm, rabatt_hoehe: e.target.value })}
                  disabled={angebotForm.rabatt_art === "keiner"}
                  data-testid="rabatt-hoehe"
                />
              </div>
              <div>
                <Label>Gültig bis</Label>
                <Input
                  type="date"
                  value={angebotForm.gueltig_bis}
                  onChange={(e) => setAngebotForm({ ...angebotForm, gueltig_bis: e.target.value })}
                  data-testid="gueltig-bis"
                />
              </div>
            </div>

            {/* Angebotstext + interne Notiz */}
            <div>
              <Label>Angebotstext (optional)</Label>
              <Textarea
                rows={2}
                placeholder="Text, der oben ins Angebot-PDF gedruckt wird"
                value={angebotForm.angebot_text}
                onChange={(e) => setAngebotForm({ ...angebotForm, angebot_text: e.target.value })}
                data-testid="angebot-text"
              />
            </div>
            <div>
              <Label>Interne Notiz (nicht auf PDF)</Label>
              <Textarea
                rows={2}
                value={angebotForm.notiz}
                onChange={(e) => setAngebotForm({ ...angebotForm, notiz: e.target.value })}
                data-testid="angebot-notiz"
              />
            </div>

            {/* Summen */}
            <div className="bg-[#F7F5F2] border border-[#F6E3DA] rounded-lg p-3">
              <div className="flex justify-between text-sm mb-1">
                <span>Zwischensumme:</span>
                <span className="font-medium">{angebotZwischensumme().toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
              </div>
              {angebotForm.rabatt_art !== "keiner" && (
                <div className="flex justify-between text-sm mb-1 text-[#9A3412]">
                  <span>Rabatt:</span>
                  <span>-{angebotRabattBetrag().toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold border-t border-[#EBC9B9] pt-2 mt-1">
                <span>Netto:</span>
                <span data-testid="netto-summe">{angebotNetto().toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAngebotDialogOpen(false)} disabled={angebotSaving}>
              <X size={14} className="mr-1" />
              Abbrechen
            </Button>
            <Button onClick={generateAngebotPdf} disabled={angebotSaving} data-testid="btn-generate-pdf">
              <Save size={14} className="mr-1" />
              {angebotSaving ? "PDF wird erstellt..." : "PDF erstellen & speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aktivitäts-Dialog */}
      <Dialog open={aktDialogOpen} onOpenChange={setAktDialogOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="akt-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
              {(() => {
                const Icon = KANAL_ICON[aktForm.kontaktkanal] || FileText;
                return (
                  <span className="flex items-center gap-2">
                    <Icon size={18} />
                    Aktivität protokollieren — {KANAL_LABEL[aktForm.kontaktkanal]}
                  </span>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Datum</Label>
                <Input
                  type="date"
                  value={aktForm.kontaktdatum}
                  onChange={(e) => setAktForm({ ...aktForm, kontaktdatum: e.target.value })}
                  data-testid="akt-datum"
                />
              </div>
              <div>
                <Label>Art</Label>
                <Select value={aktForm.kontaktart} onValueChange={(v) => setAktForm({ ...aktForm, kontaktart: v })}>
                  <SelectTrigger data-testid="akt-art">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="akquise">Akquise</SelectItem>
                    <SelectItem value="bestandskunde">Bestandskunde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Ansprechpartner</Label>
              <Input
                value={aktForm.ansprechpartner}
                onChange={(e) => setAktForm({ ...aktForm, ansprechpartner: e.target.value })}
                placeholder="Wen hast du erreicht/gesprochen?"
                data-testid="akt-ansprechpartner"
              />
            </div>
            <div>
              <Label>Gesprächsinhalt / Notiz</Label>
              <Textarea
                rows={3}
                value={aktForm.gespraechsinhalt}
                onChange={(e) => setAktForm({ ...aktForm, gespraechsinhalt: e.target.value })}
                placeholder="Worum ging es? Wichtige Punkte?"
                data-testid="akt-inhalt"
              />
            </div>
            <div>
              <Label>Ergebnis / Fazit</Label>
              <Input
                value={aktForm.ergebnis}
                onChange={(e) => setAktForm({ ...aktForm, ergebnis: e.target.value })}
                placeholder="z.B. Interesse an LG1/LG2, will Angebot"
                data-testid="akt-ergebnis"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nächster Schritt</Label>
                <Input
                  value={aktForm.naechster_schritt}
                  onChange={(e) => setAktForm({ ...aktForm, naechster_schritt: e.target.value })}
                  placeholder="z.B. Rückruf"
                  data-testid="akt-nschritt"
                />
              </div>
              <div>
                <Label>Nächster Kontakt am</Label>
                <Input
                  type="date"
                  value={aktForm.naechster_kontakt_am}
                  onChange={(e) => setAktForm({ ...aktForm, naechster_kontakt_am: e.target.value })}
                  data-testid="akt-nkontakt"
                />
              </div>
            </div>
            {aktForm.naechster_kontakt_am && (
              <p className="text-xs text-[#5a4a35] italic bg-[#F7F5F2] p-2 rounded border border-[#F6E3DA]">
                → Dieses Datum wird automatisch als Wiedervorlage für die Einrichtung gesetzt.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAktDialogOpen(false)}>
              <X size={14} className="mr-1" />
              Abbrechen
            </Button>
            <Button onClick={saveAktivitaet} data-testid="save-akt-btn">
              <Save size={14} className="mr-1" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adress- / Stammdaten-Dialog */}
      <Dialog open={addrDialogOpen} onOpenChange={setAddrDialogOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="addr-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Adresse & Stammdaten bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Straße & Hausnummer</Label>
              <Input value={addrForm.strasse} onChange={(e) => setAddrForm({ ...addrForm, strasse: e.target.value })} data-testid="addr-strasse" />
            </div>
            <div className="grid grid-cols-[100px_1fr] gap-3">
              <div>
                <Label>PLZ</Label>
                <Input value={addrForm.plz} onChange={(e) => setAddrForm({ ...addrForm, plz: e.target.value })} data-testid="addr-plz" />
              </div>
              <div>
                <Label>Ort</Label>
                <Input value={addrForm.ort} onChange={(e) => setAddrForm({ ...addrForm, ort: e.target.value })} data-testid="addr-ort" />
              </div>
            </div>
            <div>
              <Label>Telefon (Zentrale)</Label>
              <Input value={addrForm.ansprechpartner_telefon} onChange={(e) => setAddrForm({ ...addrForm, ansprechpartner_telefon: e.target.value })} data-testid="addr-tel" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrDialogOpen(false)}>
              <X size={14} className="mr-1" />
              Abbrechen
            </Button>
            <Button onClick={saveAddr} data-testid="save-addr-btn">
              <Save size={14} className="mr-1" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PflegeeinrichtungDetail;
